import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { AdminColors } from '../../../Admin/constants/adminColors';
import FontFamily from '../../../../constants/fonts';
import { OBS_STEPS, ObsStep } from './useRoutineObservation';

interface StepProgressProps {
  /** Currently active step (0-3) */
  step: ObsStep;
  /** Highest step ever reached — gates which tabs are clickable */
  reached: ObsStep;
  /** Called when user taps an unlocked tab */
  onStep: (s: ObsStep) => void;
}

/**
 * StepProgress
 *
 * 4-button horizontal tab stepper matching HTML .people-obs-progress /
 * .people-obs-step. Tabs are locked (non-interactive) until the user has
 * navigated there via Next. Once reached, they are freely clickable.
 *
 * States:
 *   active   — currently visible step (blue border + blue circle)
 *   complete — step index < active (blue-75 bg + blue border + blue circle)
 *   default  — not yet reached (grey circle, grayed out)
 *   locked   — not yet reached: opacity 0.55, cursor not-allowed
 */
const StepProgress: React.FC<StepProgressProps> = ({ step, reached, onStep }) => {
  return (
    <View style={styles.row}>
      {OBS_STEPS.map((s, i) => {
        const isActive   = i === step;
        const isComplete = i < step;
        const isLocked   = i > reached;
        const isDisabled = isLocked || isActive;

        return (
          <TouchableOpacity
            key={s.title}
            style={[
              styles.step,
              isActive   && styles.stepActive,
              isComplete && styles.stepComplete,
              isLocked   && styles.stepLocked,
            ]}
            onPress={() => !isDisabled && onStep(i as ObsStep)}
            disabled={isDisabled}
            accessibilityRole="tab"
            accessibilityState={{ selected: isActive, disabled: isDisabled }}
            accessibilityLabel={
              isLocked
                ? `${s.title} — opens once earlier stages have been visited`
                : `Go to ${s.title}`
            }
          >
            {/* Number circle */}
            <View style={[
              styles.circle,
              (isActive || isComplete) && styles.circleActive,
            ]}>
              {isComplete ? (
                <MaterialIcons name="check" size={14} color="#fff" />
              ) : (
                <Text style={[
                  styles.circleText,
                  (isActive || isComplete) && styles.circleTextActive,
                ]}>
                  {i + 1}
                </Text>
              )}
            </View>

            {/* Title + subtitle */}
            <View style={styles.textBlock}>
              <Text style={[
                styles.title,
                isActive   && styles.titleActive,
                isComplete && styles.titleComplete,
              ]} numberOfLines={1}>
                {s.title}
              </Text>
              <Text style={[
                styles.sub,
                (isActive || isComplete) && styles.subActive,
              ]} numberOfLines={1}>
                {s.sub}
              </Text>
            </View>
          </TouchableOpacity>
        );
      })}
    </View>
  );
};

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    gap: 10,
  },

  // ── Step tile ──────────────────────────────────────────────────────────
  step: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
    minHeight: 54,
    paddingHorizontal: 13,
    paddingVertical: 11,
    backgroundColor: AdminColors.bgSurface,
    borderWidth: 1,
    borderColor: AdminColors.borderTable,    // nav-blue-100
    borderRadius: 11,
  },
  stepActive: {
    borderColor: AdminColors.blue700,        // nav-blue-500 ≈ blue700
    backgroundColor: AdminColors.blue25,
    // inset shadow approximated with an extra border
    shadowColor: AdminColors.blue700,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.35,
    shadowRadius: 0,
    elevation: 0,
  },
  stepComplete: {
    borderColor: AdminColors.blue200,        // nav-blue-300 ≈ blue200
    backgroundColor: AdminColors.blue75,
  },
  stepLocked: {
    opacity: 0.55,
  },

  // ── Number circle ──────────────────────────────────────────────────────
  circle: {
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: AdminColors.nearWhite,  // nav-gray-50
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  circleActive: {
    backgroundColor: AdminColors.blue600,    // nav-blue-600
  },
  circleText: {
    fontFamily: FontFamily.bold,
    fontSize: 13,
    color: AdminColors.textPrimary,
  },
  circleTextActive: {
    color: '#fff',
  },

  // ── Text ───────────────────────────────────────────────────────────────
  textBlock: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontFamily: FontFamily.bold,
    fontSize: 13,
    color: AdminColors.textPrimary,
  },
  titleActive: {
    color: AdminColors.blue900,
  },
  titleComplete: {
    color: AdminColors.blue900,
  },
  sub: {
    fontFamily: FontFamily.regular,
    fontSize: 11,
    color: AdminColors.textSecondary,
    marginTop: 2,
  },
  subActive: {
    color: AdminColors.blue900,
  },
});

export default StepProgress;
